import { supabase } from '@/integrations/supabase/client';

/**
 * PRECO-PADRAO (I.160) — "UMA VERDADE": o preço de venda e de locação mora na linha da tabela nativa "Preço Padrão";
 * o cadastro do produto (products.sale_price / rental_price) é só espelho, preenchido por gatilho no banco.
 * Todos os caminhos que gravavam o preço no cadastro passam por aqui.
 */

/** Cria produtos e as linhas da "Preço Padrão" na mesma transação (função do banco). Devolve os ids na ordem. */
export async function createProductsWithDefaultPrice(products: Array<Record<string, unknown>>): Promise<string[]> {
  const { data, error } = await supabase.rpc('create_products_with_default_price' as any, { p_products: products } as any);
  if (error) throw error;
  return ((data as unknown as string[]) ?? []).map(String);
}

/** id da "Preço Padrão" da empresa (null se ainda não existir). */
export async function getDefaultPriceTableId(companyId: string): Promise<string | null> {
  const { data, error } = await (supabase
    .from('price_tables')
    .select('id')
    .eq('company_id', companyId) as any)
    .eq('is_default', true)
    .maybeSingle();
  if (error) throw error;
  return (data as { id: string } | null)?.id ?? null;
}

/**
 * Grava venda e/ou locação na linha da "Preço Padrão" do produto — só as colunas informadas (a outra fica como está).
 * Se a linha faltar, ela nasce com o valor informado e a outra coluna copiada do cadastro.
 */
export async function setDefaultPrices(
  companyId: string,
  productId: string,
  prices: { sale_price?: number | null; rental_price?: number | null },
): Promise<void> {
  const changes: Record<string, number | null> = {};
  if (prices.sale_price !== undefined) changes.sale_table_price = prices.sale_price;
  if (prices.rental_price !== undefined) changes.rental_table_price = prices.rental_price;
  if (Object.keys(changes).length === 0) return;

  const tableId = await getDefaultPriceTableId(companyId);
  if (!tableId) throw new Error('A tabela Preço Padrão da empresa não foi encontrada.');

  const { data: updated, error } = await supabase
    .from('product_prices')
    .update(changes as any)
    .eq('product_id', productId)
    .eq('price_table_id', tableId)
    .eq('company_id', companyId)
    .select('id');
  if (error) throw error;
  if ((updated ?? []).length > 0) return;

  const { data: product, error: productError } = await supabase
    .from('products')
    .select('sale_price, rental_price')
    .eq('id', productId)
    .eq('company_id', companyId)
    .single();
  if (productError) throw productError;
  const { error: insertError } = await supabase.from('product_prices').insert({
    company_id: companyId,
    product_id: productId,
    price_table_id: tableId,
    sale_table_price: prices.sale_price !== undefined ? prices.sale_price : product?.sale_price ?? null,
    rental_table_price: prices.rental_price !== undefined ? prices.rental_price : product?.rental_price ?? null,
  } as any);
  if (insertError) throw insertError;
}
